import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { IProduct } from 'src/app/features/produits/models/products.models';
import { Observable, of, Subscription } from 'rxjs';
import { FormBuilder, FormControl } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { IParseProduct, IQuotationDetail } from '../../models/quotations.models';
import { FilterDialogComponent } from 'src/app/features/produits/components/filter-dialog/filter-dialog.component';
import { IInventoryFilter } from 'src/app/features/produits/models/products.models';
import { MatDialog } from '@angular/material/dialog';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { ICurrency } from 'src/app/features/customers/models/customers.models';
import { IPriceBookViewDTO } from 'src/app/features/pricebook/models/pricebook.model';
import { PricebookService } from 'src/app/features/pricebook/services/pricebook.service';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { ICatalog } from 'src/app/features/catalogs/models/catalogs.models';
import { FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../../../../shared/shared.module';
import { LocalStorageService } from 'src/app/shared/localstorage.service';

type SortDirection = 'asc' | 'desc' | null;
type SortField =
  | 'name'
  | 'sales_price'
  | 'stock'
  | 'discount_product'
  | 'final_price'
  | 'price_unit'
  | 'cost'
  | null;

@Component({
  selector: 'app-products-quote-from-catalog',
  templateUrl: './products-quote-from-catalog.component.html',
  styleUrls: ['./products-quote-from-catalog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
  ],
})
export class ProductsQuoteFromCatalogComponent implements OnInit {
  @Input() existingProducts: IParseProduct[] | undefined = [];
  @Input() pricelist_name?: string;
  @Input() currency?: ICurrency;
  @Output() selectedProducts = new EventEmitter<IProduct[]>();

  quotation$: Observable<IQuotationDetail | undefined> = this.store.select(state => state.quotations.quotation);
  quotation: IQuotationDetail | undefined = undefined;  
  catalog$: Observable<ICatalog | null> = this.store.select(state => state.catalogs.catalog);
  catalog: ICatalog | null = null;
  isMarginOnCost: boolean = false; // Detect if catalog uses margin_on_cost pricing rule

  products$: Observable<IProduct[]> = of([]);
  products: IProduct[] = [];
  currentFilter: IProduct[] = [];
  subscriptions: Subscription[] = [];
  nameControleProduct = new FormControl('');
  baseUrl = environment.BASE_URL_API;
  placeholder = 'assets/images/placeholder.png';
  selectAll: boolean = false;
  productsfilter: any | undefined = undefined;
  isProductsFiltered: boolean = false;
  quantityForm: FormGroup;
  quoteProductsData: any = { currency: { symbol: '' } };
  // Sorting properties
  currentSortField: SortField = null;
  currentSortDirection: SortDirection = null;
  default_currency: ICurrency = LocalStorageService.get('currentUser')?.user?.currency!;
  selectedCatalogProducts: IProduct[] = [];

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private pricebookService: PricebookService,
    public featureFlags: FeatureFlagService
  ) {
    this.quantityForm = this.fb.group({});
  }

  ngOnInit(): void {
    const userCurrency = LocalStorageService.get('currentUser')?.user?.currency;
    const userCurrencySymbol = userCurrency?.symbol ?? '€';

    this.products$ = this.store.select((state) => state.catalogs.products);
    const sub0 = this.quotation$.subscribe(quotation => {
      this.quotation = quotation;

    });
    const sub3 = this.catalog$.subscribe(catalog => {
      this.catalog = catalog;
      // Detect if catalog uses margin_on_cost pricing rule
      this.isMarginOnCost = catalog?.overall_pricing_rule === 'margin_on_cost';
    });

    const sub1 = this.products$.subscribe(products => {
      this.products = products.map(product => {
        const defaultCurrency = {
          amount: product.final_price ?? product.sales_price,
          symbol: userCurrencySymbol
        };

        return {
          ...product,
          quantity: this.existingProducts?.find((p: any) => p.id === product.id)?.quantity || product.quantity || product.min_qte || 0,
          default_currency: defaultCurrency,
          disabled:
            product.status === 'Draft' ||
            (
              (!product.stock || (product.stock && product.stock.quantity < product.min_qte!)) &&
              product.is_owned &&
              product.can_be_sold_out_of_stock === false
            ),
          selected: this.existingProducts?.some(existingProduct => existingProduct.id === product.id)
        };
      });

      this.applyFilter();

      this.products.forEach(product => {
        this.quantityForm.addControl('quantity_' + product.product_id, this.fb.control(0));
        this.quantityForm.addControl('final_price_' + product.product_id, this.fb.control(product.final_price || product.sales_price || 0));
      });
    });

    const sub2 = this.nameControleProduct.valueChanges.subscribe(() => this.applyFilter());
    this.subscriptions.push(sub0, sub1, sub2, sub3);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  setFilter() {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      minWidth: '650px',
      width: '90vw',
      height: '80vh',
      data: {
        filter: this.productsfilter,
        isQuoteProducts: true
      }
    });

    // Handle selected products filter locally
    dialogRef.afterClosed().subscribe((filter: IInventoryFilter) => {
      if (filter) {
        this.isProductsFiltered = this.isFiltered(filter);
        this.applyFilter();
      }
    })
  }

  isFiltered(filter: any) {
    if (filter) {
      const parsedFilter = { ...filter.inventoryFilter, ...filter.productsInfoFilter, ...filter.organizeFilter };
      this.productsfilter = parsedFilter;

      const isFiltered = !!(
        (parsedFilter.name && parsedFilter.name !== "") ||
        (parsedFilter.sku && parsedFilter.sku !== "") ||
        (parsedFilter.barcode && parsedFilter.barcode !== "") ||
        (parsedFilter.brands && parsedFilter.brands.length > 0) ||
        parsedFilter.status ||
        parsedFilter.has_variants ||
        parsedFilter.sales_price_min != null ||
        parsedFilter.sales_price_max != null ||
        parsedFilter.cost_min != null ||
        parsedFilter.cost_max != null ||
        parsedFilter.quantity_min != null ||
        parsedFilter.quantity_max != null ||
        parsedFilter.reorder_point_min != null ||
        parsedFilter.reorder_point_max != null ||
        (parsedFilter.categories && parsedFilter.categories.length > 0) ||
        (parsedFilter.tags && parsedFilter.tags.length > 0) ||
        parsedFilter.has_catalogs ||
        parsedFilter.number_of_catalogs != null
      );

      return isFiltered
    }

    return false;
  }

  applyFilter(): void {
    const filter = this.productsfilter;

    // First apply name/SKU filter
    let filteredProducts = this.products.filter(product => {
      const nameOrSkuMatches = !this.nameControleProduct.value ||
        product.name?.toLowerCase().includes(this.nameControleProduct.value?.trim().toLowerCase()) ||
        product.sku?.toLowerCase().includes(this.nameControleProduct.value?.trim().toLowerCase());
      return nameOrSkuMatches;
    });

    // Then apply advanced filters if they exist
    if (filter) {
      filteredProducts = filteredProducts.filter(product =>
        (filter.name === undefined || product.name?.toLowerCase().includes(filter.name.toLowerCase())) &&
        (filter.sku === undefined || product.sku?.toLowerCase().includes(filter.sku.toLowerCase())) &&
        (filter.status === undefined || product.status === filter.status) &&
        (filter.brands === undefined || filter.brands.length === 0 || filter.brands.some((brand: any) => brand.id === product.brand?.id)) &&
        (filter.has_variants === undefined ||
          (product.variants?.length! > 0 && filter.has_variants === 'true') ||
          (!product.variants?.length && filter.has_variants === 'false')) &&
        (filter.sales_price_min === undefined || product.sales_price >= filter.sales_price_min) &&
        (filter.sales_price_max === undefined || product.sales_price <= filter.sales_price_max) &&
        (filter.cost_min === undefined || product.cost >= filter.cost_min) &&
        (filter.cost_max === undefined || product.cost <= filter.cost_max) &&
        (filter.quantity_min === undefined || (product.stock?.quantity ?? 0) >= filter.quantity_min) &&
        (filter.quantity_max === undefined || (product.stock?.quantity ?? 0) <= filter.quantity_max) &&
        (filter.reorder_point_min === undefined || (product.stock?.reorder_point ?? 0) >= filter.reorder_point_min) &&
        (filter.reorder_point_max === undefined || (product.stock?.reorder_point ?? 0) <= filter.reorder_point_max) &&
        (filter.categories === undefined || filter.categories.length === 0 || product.categories?.some(cat => filter.categories.map((category: ICategory) => category.id).includes(cat.id))) &&
        (filter.tags === undefined || filter.tags.length === 0 || product.tags?.some(tag => filter.tags.some((fTag: any) => fTag.id === tag.id))) &&
        (filter.has_catalogs === undefined || (product.catalogs?.length! > 0) === filter.has_catalogs) &&
        (filter.number_of_catalogs === undefined || product.catalogs?.length === filter.number_of_catalogs)
      );
    }

    // Update currentFilter with the filtered results
    this.currentFilter = filteredProducts;
    this.applySorting();
  }

  clearFilter() {
    this.isProductsFiltered = false
    this.productsfilter = undefined
    this.applyFilter()
  }

  sort(field: SortField): void {
    if (this.currentSortField === field) {
      // Toggle direction if clicking the same field
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // Set new field and default to ascending
      this.currentSortField = field;
      this.currentSortDirection = 'asc';
    }
    this.applySorting();
  }

  private applySorting(): void {
    if (!this.currentSortField || !this.currentSortDirection) {
      return;
    }

    this.currentFilter.sort((a, b) => {
      let valueA: any;
      let valueB: any;

      switch (this.currentSortField) {
        case 'name':
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
          break;
        case 'sales_price':
          valueA = a.sales_price || 0;
          valueB = b.sales_price || 0;
          break;
        case 'cost':
          // Get cost from main currency or fallback to product.cost
          const costA = a.currencies?.find(c => c.is_main)?.cost ?? a.cost ?? 0;
          const costB = b.currencies?.find(c => c.is_main)?.cost ?? b.cost ?? 0;
          valueA = costA;
          valueB = costB;
          break;
        case 'stock':
          // Special handling for stock sorting
          if (a.is_owned !== b.is_owned) {
            // If one is owned and the other isn't, owned products go to the bottom in ascending order
            return this.currentSortDirection === 'asc'
              ? (a.is_owned ? 1 : -1)  // owned products go to bottom in ascending
              : (a.is_owned ? -1 : 1); // owned products go to top in descending
          }
          // If both have same ownership status, sort by stock quantity
          valueA = a.stock?.quantity || 0;
          valueB = b.stock?.quantity || 0;
          break;
        default:
          return 0;
      }

      if (valueA < valueB) {
        return this.currentSortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.currentSortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }
  updateDiscount(event: any, product: any): void {
    console.log('updateDiscount event: ', event);
    const discount = Number(event.target.value);
    product.discount = discount;
    const price = product.sales_price || 0;
    product.final_price = price - (price * discount) / 100;
    this.emitProduct(product);
  }

  updateFinalPrice(event: any, product: any): void {
    const value = Number(event.target.value);
    product.final_price = value;
    this.emitProduct(product);
  }

  updateQuantity(event: any, product: any): void {
    const value = Number(event.target.value);
    const control = this.quantityForm.get('quantity_' + product.product_id);
    if (control) control.setValue(value);
    product.quantity = value;
    this.emitProduct(product);
  }
  
  getProductFinalPrice(product: IProduct): string {
    const mainCurrency = product.currencies?.find(c => c.is_main);
    const symbol = product.default_currency?.symbol ?? '€';

    if (mainCurrency?.final_price != null) {
      return `${mainCurrency.final_price} ${symbol}`;
    }

    if (product.final_price != null) {
      return `${product.final_price} ${symbol}`;
    }

    return '--';
  }

  getRetailPrice(product: IProduct): string {
    const mainCurrency = product.currencies?.find(c => c.is_main);
    const symbol = product.default_currency?.symbol ?? '€';

    const price =
      (mainCurrency?.sales_price != null ? mainCurrency.sales_price : product.sales_price);

    if (price != null) {
      return `${price} ${symbol}`;
    }

    return '--';
  }

  getProductCost(product: IProduct): string {
    const mainCurrency = product.currencies?.find(c => c.is_main);
    const symbol = product.default_currency?.symbol ?? '€';

    // Try to get cost from currency first, then fallback to product.cost
    const cost = mainCurrency?.cost != null ? mainCurrency.cost : product.cost;

    if (cost != null && cost > 0) {
      return `${cost} ${symbol}`;
    }

    return '--';
  }

  isStockAlert(product: any): boolean {
    return product.is_owned && product.stock && product.stock.quantity < product.min_qte;
  }

  alertType(product: any): string {
    if (product.stock?.quantity === 0) return 'alert_div_danger';
    if (product.stock?.quantity < product.min_qte) return 'alert_div_warning';
    return '';
  }

  alertMessage(product: any): string {
    if (product.stock?.quantity === 0) return 'quotations_and_orders.out_of_stock';
    if (product.stock?.quantity < product.min_qte) return 'quotations_and_orders.low_stock';
    return '';
  }

  toggleSelectAll(): void {
    const newState = !this.selectAll;
    this.currentFilter = this.currentFilter.map(p => ({ ...p, selected: !p.disabled && newState }));
    this.products = this.products.map(p => ({ ...p, selected: !p.disabled && newState }));
    this.selectAll = this.currentFilter.filter(p => !p.disabled).every(p => p.selected);
    this.updateSelectedListAndEmit();
  }

  toggleSelectProduct(product: IProduct): void {
    if (product.disabled) return;
    product.selected = !product.selected;

    if (product.selected) {
      product.quantity = product.quantity || product.min_qte || 1;
      this.quantityForm.get('quantity_' + product.product_id)?.setValue(product.quantity);
    }

    this.products = this.products.map(p => p.product_id === product.product_id ? { ...p, selected: product.selected } : p);
    this.selectAll = this.currentFilter.filter(p => !p.disabled).every(p => p.selected);
    this.updateSelectedListAndEmit();
  }

  updateSelectedListAndEmit(): void {
    this.selectedCatalogProducts = this.products.filter(p => p.selected && !p.disabled);
    this.selectedProducts.emit(this.selectedCatalogProducts);
  }

  emitProduct(product: IProduct): void {
    if (!product.disabled) {
      product.selected = true;
      if (!product.quantity || product.quantity <= 0) {
        product.quantity = product.quantity || product.min_qte || 1;
        const control = this.quantityForm.get('quantity_' + product.product_id);
        if (control) control.setValue(product.quantity);
      }
      this.updateSelectedListAndEmit();
    }
  }

  getSelectedProductsCount(): number {
    return this.products.filter(product => product.selected).length;
  }

  getGifts() {
    const gifts = [...(this.catalog?.best_deal != null ? this.catalog.best_deal : []), this.catalog?.best_free_shipping].filter(
      (deal): deal is IPriceBookViewDTO => deal !== null
    );

    return gifts;
  }

  getProductGifts(product: IProduct): IPriceBookViewDTO[] {
    const gifts: IPriceBookViewDTO[] = [];

    const hasDeal = (deal: IPriceBookViewDTO) =>
      gifts.some(d => d.id === deal.id);

    const deals = product.best_deal as IPriceBookViewDTO[] | null;
    if (deals && deals.length > 0) {
      for (const deal of deals) {
        if (!hasDeal(deal)) {
          gifts.push(deal);
        }
      }
    }
    
    const buyXGetYDeal = product.best_buy_x_get_y as IPriceBookViewDTO[] | null;
    if (buyXGetYDeal && buyXGetYDeal.length > 0) {
      for (const deal of buyXGetYDeal) {
        if (!hasDeal(deal)) {
          gifts.push(deal);
        }
      }
    }

    return gifts;
  } 
}
